import { parse, isAfter, isBefore } from 'date-fns'
import { convertDurationToMinutes } from "./DateTimeHelper";
import { validateTimeRange } from "../Admin/Bookings/Forms/Validation/AvailabilityValidationSchema";
import _ from "lodash";
import { FieldTypes } from "../Modules/FormLogs/FormLogs";

/**
 * For some multi-select inputs we might need to distinguish between different
 * 'types' of options, eg in a multi-select with different optgroups.
 * This function will add a prefix to all the form option values so that the
 * server can know which 'type' of option has been selected
 */
export const prefixFormOptions = (options, prefix) => {
    return options.map(function(item) {
        return {
            value: `${prefix}:${item.value}`,
            label: item.label
        }
    });
}

export const retrieveMultiFilterValuesByPrefix = (filterItems, prefix) => {
    if (!filterItems) {
        return [];
    }

    return filterItems
        .filter(item => item.value.startsWith(prefix))
        .map(item => item.value.replace(prefix + ':', ''))
    ;
}

/**
 * JMAC - Copy/pasted here from feat/bookings branch - please don't be alarmed
 */
export const objectToFormData = function(obj, form, namespace, masterNamespace) {

    var fd = form || new FormData();
    var formKey;

    for(var property in obj) {
        if(obj.hasOwnProperty(property)) {
            if(namespace) {
                if (masterNamespace) {
                    formKey = masterNamespace + '[' + namespace + '][' + property + ']';
                } else {
                    formKey = namespace + '[' + property + ']';
                }
            } else {
                formKey = property;
            }

            // if the property is an object, but not a File,
            // use recursivity.
            if(typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
                if (namespace) {
                    objectToFormData(obj[property], fd, property, (masterNamespace ? masterNamespace + '[' + namespace + ']' : namespace));
                } else {
                    objectToFormData(obj[property], fd, property);
                }
            } else {
                // if it's a string or a File object
                fd.append(formKey, obj[property]);
            }
        }
    }
    return fd;
}

/**
 * Some entities with a custom form attached (ie bookings) might
 * need to process some of the data before being handed over to the front end.
 * At the moment, only the image upload fields need this treatment.
 * @param entity
 * @returns {{form}|*}
 */
export const parseEntityWithCustomForm = (entity) => {
    // Trap entities which might have a custom form attached, but don't in this case
    if (!entity.form || !entity.formConfig || !entity.formConfig.fields) {
        return entity;
    }

    let parsedEntity = {...entity};

    parsedEntity.formConfig.fields.map(fieldConfig => {
        if (!entity.form.hasOwnProperty(fieldConfig.id)) {
            return;
        }

        let field = entity.form[fieldConfig.id];

        if (fieldConfig.type === FieldTypes.TYPE_FILEUPLOAD) {
            let file = dataUrlToFile(field.data, field.filename, field.id);
            parsedEntity.form[fieldConfig.id] = [file];
        }
    })

    return parsedEntity;
}

/**
 * Convert BinaryDataResource data to a JS File object
 */
export const dataUrlToFile = function(dataurl, filename, id) {
    var arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1]),
        n = bstr.length,
        u8arr = new Uint8Array(n);

    while(n--){
        u8arr[n] = bstr.charCodeAt(n);
    }

    let file = new File([u8arr], filename, { type: mime });
    return Object.assign(file, {
        id: id,
        url: URL.createObjectURL(file)
    });
}

/**
 * @deprecated
 * @todo REMOVE
 * @param values
 * @param defaultConfig
 * @param isCategoryConfig
 * @returns {{}}
 */
export const validateBookingsConfig = (values, defaultConfig, isCategoryConfig = false) => {
    let errors = {};

    let configTimeslotValue;

    values.configs.map((config, index) => {
        let configClone = {...config};
        configClone.isExpanded = defaultConfig.isExpanded;

        if (configClone.id == 0 && _.isEqual(configClone, defaultConfig)) {
            return true;
        }

        var configErrors = {};

        if (!configTimeslotValue) {
            configTimeslotValue = config.max_simultaneous;
        }

        if (configTimeslotValue != config.max_simultaneous) {
            configErrors.max_simultaneous = 'Must be the same across all configurations';
        }

        // Some fields we don't care about if this is a category config
        if (!isCategoryConfig) {
            if (!config.name) {
                configErrors.name = 'Name is required';
            }

            var duration = config.duration;
            if (!Number.isInteger(duration)) {
                duration = convertDurationToMinutes(duration);
            }

            if (duration <= 0) {
                configErrors.duration = 'Duration must be greater than zero minutes';
            } else if (duration % 15 != 0) {
                configErrors.duration = 'Duration must be in 15 minute increments';
            }
        }

        if (config.buffer_check) {
            var buffer = config.buffer;
            if (!Number.isInteger(buffer)) {
                buffer = convertDurationToMinutes(buffer);
            }

            if (buffer <= 0) {
                configErrors.buffer = 'Buffer must be greater than zero minutes';
            } else if (buffer % 5 != 0) {
                configErrors.buffer = 'Buffer must be in 5 minute increments';
            }
        }

        if (config.availability) {
            let availabilityErrors = validateTimeRange(config.availability);

            if (Object.keys(availabilityErrors).length > 0) {
                configErrors.availability = availabilityErrors;
            }
        }

        if (config.overrides) {
            let overrideErrors = validateTimeRange(config.overrides);

            if (Object.keys(overrideErrors).length > 0) {
                configErrors.overrides = overrideErrors;
            }
        }

        if (Object.keys(configErrors).length > 0) {
            errors = {
                ...errors,
                [index]: configErrors
            }
        }
    });
    return errors;
}

export const formatBookingsConfig = (data, defaultConfig) => {
    return data.configs.map((config, index) => {
        let cfg = {...config};

        cfg.isExpanded = defaultConfig.isExpanded;
        if (cfg.id == 0 &&  _.isEqual(cfg, defaultConfig)) {
            return false;
        }

        cfg.restrictions.blocks = cfg.restrictions.blocks.map(block => block.value);
        cfg.restrictions.groups = cfg.restrictions.groups.map(group => group.value);
        cfg.restrictions.units = cfg.restrictions.units.map(unit => unit.value);

        if (!Number.isInteger(cfg.duration)) {
            cfg.duration = convertDurationToMinutes(cfg.duration);
        }

        if (!cfg.buffer_check) {
            cfg.buffer = 0;
        } else if (!Number.isInteger(cfg.buffer)) {
            cfg.buffer = convertDurationToMinutes(cfg.buffer);
        }

        if (typeof(cfg.availability) !== 'object') {
            cfg.availability = JSON.parse(cfg.availability);
        }

        if (!Number.isInteger(cfg.order)) {
            cfg.order = index;
        }

        cfg.buffer_check = (cfg.buffer > 0);

        formatAvailabilitiesForSubmit(cfg.availability);
        formatAvailabilitiesForSubmit(cfg.overrides);

        return cfg;
    }).filter(config => config);
}

/**
 * Iterate an availabilities object
 *
 * The most important thing that happens here is that an
 * empty array for a day (ie a day without any availabilities)
 * is given an empty string in the array. This is because empty arrays
 * are excluded when converting to formdata, which would
 * @param availabilities
 */
const formatAvailabilitiesForSubmit = (availabilities) => {
    if (!availabilities) {
        return;
    }

    Object.keys(availabilities).map(date => {
        if (Array.isArray(availabilities[date])) {
            availabilities[date] = availabilities[date];
            if (!availabilities[date].length) {
                availabilities[date] = [''];
            }
        } else {
            availabilities[date] = (availabilities[date])
                ? availabilities[date].split(/[,|\n]\s?/)
                : []
            ;
        }
    });
}
